import type { MessageCategory } from "@/hooks/useMessages";

export type SelectableCategory = Exclude<MessageCategory, "general">;

interface CategoryDescriptor {
  value: SelectableCategory;
  label: string;
  description: string;
  // Pill color (selected state)
  selectedClass: string;
  // Pill color (unselected state)
  unselectedClass: string;
}

export const CATEGORIES: CategoryDescriptor[] = [
  {
    value: "medical",
    label: "Medical",
    description: "Symptoms, dosing, side effects, prescription questions — routed to your clinician.",
    selectedClass: "bg-fh-green-dark text-white border-fh-green-dark",
    unselectedClass: "bg-white text-fh-ink border-fh-border hover:border-fh-green",
  },
  {
    value: "support",
    label: "Support",
    description: "Account, technical, or general questions — routed to the ForteHealth care team.",
    selectedClass: "bg-fh-green-dark text-white border-fh-green-dark",
    unselectedClass: "bg-white text-fh-ink border-fh-border hover:border-fh-green",
  },
  {
    value: "billing",
    label: "Billing",
    description: "Charges, refunds, subscription changes, insurance — routed to billing support.",
    selectedClass: "bg-fh-green-dark text-white border-fh-green-dark",
    unselectedClass: "bg-white text-fh-ink border-fh-border hover:border-fh-green",
  },
];

/**
 * Three-pill category selector for "what kind of question is this?"
 *
 * Patient picks one before sending. Drives routing in patient-send-message
 * edge function (medical → clinician inbox, support+billing → care team).
 *
 * Compact variant fits inline above the textarea; full variant shows
 * descriptions below the pills (used on /messages page where there's more
 * vertical room).
 */
export function MessageCategoryPicker({
  value, onChange, variant = "compact",
}: {
  value: SelectableCategory | null;
  onChange: (next: SelectableCategory) => void;
  variant?: "compact" | "full";
}) {
  const selected = CATEGORIES.find((c) => c.value === value);

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[11px] uppercase tracking-wider font-semibold text-fh-gray">
          What's this about?
        </span>
        {variant === "compact" && selected && (
          <span className="text-[11px] text-fh-gray-light truncate">
            · {selected.description}
          </span>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {CATEGORIES.map((cat) => {
          const isSelected = cat.value === value;
          return (
            <button
              key={cat.value}
              type="button"
              onClick={() => onChange(cat.value)}
              aria-pressed={isSelected}
              className={[
                "px-3.5 py-1.5 rounded-full border text-[13px] font-semibold transition",
                isSelected ? cat.selectedClass : cat.unselectedClass,
              ].join(" ")}
            >
              {cat.label}
            </button>
          );
        })}
      </div>
      {variant === "full" && (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2">
          {CATEGORIES.map((cat) => (
            <div
              key={cat.value}
              className={[
                "text-[11.5px] leading-snug p-2 rounded-[8px]",
                cat.value === value
                  ? "bg-fh-sage/40 text-fh-ink"
                  : "text-fh-gray",
              ].join(" ")}
            >
              <strong className="block text-fh-ink mb-0.5">{cat.label}</strong>
              {cat.description}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Visual badge for a category — used on the admin /messages dashboard +
 * patient Messages thread to label each message's category.
 */
export function CategoryBadge({ category }: { category: MessageCategory }) {
  const styleByCategory: Record<MessageCategory, { bg: string; text: string; label: string }> = {
    medical: { bg: "bg-red-50",     text: "text-red-700",        label: "Medical" },
    support: { bg: "bg-fh-sage",    text: "text-fh-green-dark",  label: "Support" },
    billing: { bg: "bg-amber-50",   text: "text-amber-800",      label: "Billing" },
    general: { bg: "bg-fh-border",  text: "text-fh-gray",        label: "General" },
  };
  const s = styleByCategory[category] ?? styleByCategory.general;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider ${s.bg} ${s.text}`}
    >
      {s.label}
    </span>
  );
}
